import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Split from 'react-split';
import './Inventory.css';
import { FaSearch, FaTimes, FaTag, FaFilePdf, FaColumns, FaHistory, FaFileCsv, FaEyeSlash } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const LS_LIMIT_KEY  = 'inventoryLimit';
const LS_SORTS_KEY  = 'inventorySorts.v1';
const LS_DATE_PRESET = 'inventoryDatePreset.v1';
const LS_Q_KEY = 'inventoryQ.v1';
const LS_CLIENT_KEY = 'inventoryClientId.v1';
const LS_FROM_KEY = 'inventoryFromDate.v1';
const LS_TO_KEY = 'inventoryToDate.v1';
const LS_COLWIDTHS_KEY = 'inventoryColWidths.v1';
const LS_COLORDER_KEY = 'inventoryColOrder.v1';
const LS_HIDDEN_COLS_KEY = 'inventoryHiddenCols.v1';
const LS_STORAGEUNIT_KEY = 'inventoryStorageUnitId.v1';
const MIN_COL_W = 40;

// --- helpers to read current user / role ---
function getCurrentUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
  catch { return {}; }
}
function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function getUserClientId(u) {
  const id = u.clientid ?? u.clientId ?? u.client_id;
  return (id || id === 0) ? String(id) : '';
}
function getUserClientName(u) {
  return u.clientname ?? u.clientName ?? u.client ?? '';
}

function getInitialLimit() {
  const stored = window.localStorage.getItem(LS_LIMIT_KEY);
  return stored ? parseInt(stored, 10) : DEFAULT_LIMIT;
}
function getInitialSorts() {
  try {
    const raw = window.localStorage.getItem(LS_SORTS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed) && parsed.every(s => s && s.field && s.order)) {
      return parsed;
    }
  } catch {}
  return [{ field: 'recvdate', order: 'DESC' }];
}

function getInitialColWidths() {
  try {
    const raw = window.localStorage.getItem(LS_COLWIDTHS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(parsed)) {
        const n = parseInt(v, 10);
        if (!isNaN(n) && n > 30 && n < 1200) out[k] = n;
      }
      return out;
    }
  } catch {}
  return {};
}

function getInitialHiddenCols() {
  try {
    const raw = window.localStorage.getItem(LS_HIDDEN_COLS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) {
      return parsed.filter(k => typeof k === 'string');
    }
  } catch {}
  return [];
}

// For mapping column headers to backend fields (kept for reference)
const SORT_FIELDS = {
  sku: 'sku',
  description: 'description',
  clientname: 'clientname',
  ponum: 'ponum',
  lotnum: 'lotnum',
  receiptid: 'receiptid',
  palletnumber: 'palletnumber',
  storageunitname: 'storageunitname',
  areaname: 'areaname',
  recvdate: 'recvdate',
  qtyunitsonhand: 'qtyunitsonhand',
  piecesonhand: 'piecesonhand',
  expiration: 'expiration'
};

// ---- Date preset helpers ----
const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const startOfWeekMon = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day); // Monday as first day
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfWeekMon = (d) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return e;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear  = (y) => new Date(y, 0, 1);
const endOfYear    = (y) => new Date(y, 11, 31);

export default function Inventory() {
  const navigate = useNavigate();
  // role flags
  const user = getCurrentUser();
  const IS_CLIENT = isClientUser(user);
  const LOCKED_CLIENT_ID = getUserClientId(user);
  const LOCKED_CLIENT_NAME_FALLBACK = getUserClientName(user) || 'My Company';

  const [rows, setRows] = useState([]);
  const [exporting, setExporting] = useState(false);

  const [limit, setLimit] = useState(getInitialLimit());
  const [limitInput, setLimitInput] = useState(String(getInitialLimit()));
  const [meta, setMeta] = useState({ total: 0, limit: getInitialLimit(), offset: 0 });

  const getInitialQ = () => {
    try { return localStorage.getItem(LS_Q_KEY) || ''; } catch { return ''; }
  };
  const [q, setQ] = useState(getInitialQ());
  const [loading, setLoading] = useState(false);

  // Multi-sort state (PERSISTED)
  const [sorts, setSorts] = useState(getInitialSorts());
  useEffect(() => {
    // persist whenever sorts change
    window.localStorage.setItem(LS_SORTS_KEY, JSON.stringify(sorts));
  }, [sorts]);

  // Filters
  const getInitialClient = () => {
    if (IS_CLIENT && LOCKED_CLIENT_ID) return LOCKED_CLIENT_ID;
    try { return localStorage.getItem(LS_CLIENT_KEY) || ''; } catch { return ''; }
  };
  const [client, setClient] = useState(getInitialClient());
  const getInitialStorageUnit = () => {
    try { return localStorage.getItem(LS_STORAGEUNIT_KEY) || ''; } catch { return ''; }
  };
  const [storageUnitId, setStorageUnitId] = useState(getInitialStorageUnit);

  // Date range + preset
  const [fromDate, setFromDate] = useState(() => localStorage.getItem(LS_FROM_KEY) || '');
  const [toDate, setToDate] = useState(() => localStorage.getItem(LS_TO_KEY) || '');
  const [datePreset, setDatePreset] = useState(localStorage.getItem(LS_DATE_PRESET) || '');

  // Client dropdown options
  const [clientOptions, setClientOptions] = useState([]);
  const [storageUnitOptions, setStorageUnitOptions] = useState([]);

  // Selected inventory row
  const [selectedRow, setSelectedRow] = useState(null);

  // History
  const [invHistory, setInvHistory] = useState([]);
  const [invHistoryLoading, setInvHistoryLoading] = useState(false);
  const [palletHistory, setPalletHistory] = useState([]);
  const [palletHistoryLoading, setPalletHistoryLoading] = useState(false);
  const [selectedInvHistId, setSelectedInvHistId] = useState(null);
  const [selectedPalletHistId, setSelectedPalletHistId] = useState(null);

  // Bottom split (table | histories)
  const INV_SPLIT_KEY = 'inventory-split-sizes';
  const getInitialInvSplit = () => {
    try { const s = JSON.parse(localStorage.getItem(INV_SPLIT_KEY) || '[]'); if (Array.isArray(s) && s.length === 2) return s; } catch {}
    return [70, 30];
  };
  const [splitSizes, setSplitSizes] = useState(getInitialInvSplit);
  const [bottomOpen, setBottomOpen] = useState(false);

  // Image preview (like Order Items)
  const [preview, setPreview] = useState({ src: '', x: 0, y: 0, visible: false });
  const previewRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const placePreview = (clientX, clientY) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const margin = 12, offset = 16;
    const boxW = previewRef.current?.offsetWidth || Math.min(vw * 0.4, 520);
    const boxH = previewRef.current?.offsetHeight || Math.min(vh * 0.6, 520);
    let left = clientX + offset, top = clientY + offset;
    if (left + boxW + margin > vw) left = clientX - boxW - offset;
    if (top + boxH + margin > vh) top = clientY - boxH - offset;
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    if (left + boxW + margin > vw) left = vw - boxW - margin;
    if (top + boxH + margin > vh) top = vh - boxH - margin;
    setPreview(p => ({ ...p, x: left, y: top }));
  };
  const showPreview = (raw, e) => {
    const src = raw || resolveItemThumb({ itemimageurl: raw, image1: raw });
    if (!src) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPreview({ src, x: e.clientX, y: e.clientY, visible: true });
    requestAnimationFrame(() => placePreview(e.clientX, e.clientY));
  };
  const movePreview = (e) => {
    if (!preview.visible) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    placePreview(e.clientX, e.clientY);
  };
  const hidePreview = () => setPreview(p => ({ ...p, visible: false }));
  const onPreviewImgLoad = () => {
    if (!preview.visible) return;
    const { x, y } = lastPosRef.current;
    placePreview(x, y);
  };

  // Print a 4x6 pallet label matching Receipts/Receive
  const printPalletLabel = async (inventoryId) => {
    if (!inventoryId) return;
    try {
      const res = await fetch(`${API}/api/inventory/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventoryIds: [inventoryId] })
      });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e?.message || 'Failed to generate pallet label');
    }
  };

  // Pagination
  const page = useMemo(
    () => Math.floor((meta.offset || 0) / (meta.limit || limit || DEFAULT_LIMIT)) + 1,
    [meta.offset, meta.limit, limit]
  );
  const lastPage = useMemo(
    () => Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || limit || DEFAULT_LIMIT))),
    [meta.total, meta.limit, limit]
  );

  // Abort/race control for list fetch
  const invFetchSeq = useRef(0);
  const invFetchAbort = useRef(null);

  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const fetchInventory = async (offset = 0, lim = limit) => {
    const thisSeq = ++invFetchSeq.current;

    // abort any in-flight
    if (invFetchAbort.current) {
      try { invFetchAbort.current.abort(); } catch {}
    }
    const controller = new AbortController();
    invFetchAbort.current = controller;

    setLoading(true);
    try {
      const sortParam = sorts.map(s => `${s.field}:${s.order}`).join(',');
      const params = new URLSearchParams({ limit: lim, offset, sort: sortParam });

      if (q) params.set('q', q);

      if (client) {
        params.set('clientid', client);
      }

      if (storageUnitId) {
        params.set('storageunitid', storageUnitId);
      }

      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);

      const json = await fetchJSON(`${API}/api/inventory?${params.toString()}`, { signal: controller.signal });

      if (thisSeq !== invFetchSeq.current) return; // ignore stale
      setRows(json.data || []);
      setMeta(json.meta || { total: 0, limit: lim, offset });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      if (thisSeq !== invFetchSeq.current) return;
      setRows([]);
      setMeta({ total: 0, limit: lim, offset: 0 });
    } finally {
      if (thisSeq === invFetchSeq.current) setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/reports/filters/clients`);
      const data = res.ok ? await res.json() : [];
      const list = (Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), name: c.name }));
      setClientOptions(list);
      if (IS_CLIENT) {
        if (!list.find(x => String(x.id) === String(client))) {
          setClient(list[0]?.id || '');
        }
        if (list.length === 1) {
          setClient(list[0]?.id || '');
        }
      }
    } catch {
      setClientOptions([]);
    }
  };
  const fetchStorageUnits = async () => {
    try {
      const res = await fetch(`${API}/api/packagingunits/storageunits`);
      const rows = res.ok ? await res.json() : [];
      const list = (Array.isArray(rows) ? rows : []).map(u => ({ id: String(u.id), name: u.name || `Unit #${u.id}` }));
      setStorageUnitOptions(list);
      if (storageUnitId && !list.find(u => String(u.id) === String(storageUnitId))) {
        setStorageUnitId('');
      }
    } catch (err) {
      console.error('Failed to fetch storage units', err);
      setStorageUnitOptions([]);
    }
  };

  // Load histories when a row is selected
  useEffect(() => {
    if (!selectedRow?.inventoryid) { setInvHistory([]); setPalletHistory([]); setBottomOpen(false); return; }
    const invId = selectedRow.inventoryid;
    const palletId = selectedRow.palletid;
    // Refresh the selected row from backend to ensure latest on-hand quantities
    (async () => {
      try {
        const fresh = await fetchJSON(`${API}/api/inventory/${invId}`);
        if (fresh && fresh.inventoryid === invId) {
          setSelectedRow(prev => ({ ...(prev || {}), ...fresh }));
          // Patch the row within the table to reflect updated on-hand values
          setRows(prev => Array.isArray(prev) ? prev.map(r => (r.inventoryid === invId ? { ...r, ...fresh } : r)) : prev);
        }
      } catch {}
    })();
    setBottomOpen(true);
    setSelectedInvHistId(null);
    setSelectedPalletHistId(null);
    // Inventory history
    (async () => {
      setInvHistoryLoading(true);
      try {
        const res = await fetch(`${API}/api/inventory/${invId}/history`);
        const rows = await res.json().catch(() => []);
        setInvHistory(Array.isArray(rows) ? rows : []);
      } catch { setInvHistory([]); } finally { setInvHistoryLoading(false); }
    })();
    // Pallet location history
    if (palletId) {
      (async () => {
        setPalletHistoryLoading(true);
        try {
          const res = await fetch(`${API}/api/pallets/${palletId}/locationhistory`);
          const rows = await res.json().catch(() => []);
          setPalletHistory(Array.isArray(rows) ? rows : []);
        } catch { setPalletHistory([]); } finally { setPalletHistoryLoading(false); }
      })();
    } else {
      setPalletHistory([]);
    }
  }, [selectedRow?.inventoryid, selectedRow?.palletid]);

  // Apply saved preset on mount (before initial fetch)
  useEffect(() => {
    if (datePreset) {
      applyDatePreset(datePreset);
    } else {
      fetchInventory(0, limit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only once

  // reload on filters/sorts/limit
  useEffect(() => {
    fetchInventory(meta.offset, limit);
    // eslint-disable-next-line
  }, [sorts, client, fromDate, toDate, IS_CLIENT, limit, storageUnitId]);

  useEffect(() => { fetchClients(); }, []);
  useEffect(() => { fetchStorageUnits(); }, []);
  // Persist simple filters
  useEffect(() => { try { localStorage.setItem(LS_Q_KEY, q || ''); } catch {} }, [q]);
  useEffect(() => { try { localStorage.setItem(LS_CLIENT_KEY, client || ''); } catch {} }, [client]);
  useEffect(() => { try { localStorage.setItem(LS_FROM_KEY, fromDate || ''); } catch {} }, [fromDate]);
  useEffect(() => { try { localStorage.setItem(LS_TO_KEY, toDate || ''); } catch {} }, [toDate]);
  useEffect(() => { try { localStorage.setItem(LS_STORAGEUNIT_KEY, storageUnitId || ''); } catch {} }, [storageUnitId]);

  const gotoPage = (p) => {
    const lim = meta.limit || limit || DEFAULT_LIMIT;
    const nextOffset = Math.max(0, (p - 1) * lim);
    // clear and fetch
    setRows([]); setMeta({ total: 0, limit: lim, offset: nextOffset });
    fetchInventory(nextOffset, lim);
  };

  // Build sort param string accepted by backend (e.g., sku:ASC,recvdate:DESC)
  const buildSortParam = () => {
    try {
      if (!Array.isArray(sorts) || !sorts.length) return '';
      return sorts.map(s => `${s.field}:${(s.order || 'ASC')}`).join(',');
    } catch { return ''; }
  };

  // Export all rows matching current filters to CSV (client-side pagination)
  const exportInventoryCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const esc = (v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };

  const headers = [
        'Inventory ID','Client','SKU','Item Description','Inventory Notes','Part #','WR #','Storage Unit #','Location','UPC','Recv Date','Qty Units','Qty Per','Units On Hand','Pieces On Hand','Expiration','Lot #','Area','Warehouse','Storage Unit Name','Packaging'
      ];

        const buildParams = (limit, offset) => {
          const p = new URLSearchParams();
          if (q) p.set('q', q);
          if (client) p.set('clientid', client);
          if (storageUnitId) p.set('storageunitid', storageUnitId);
          if (fromDate) p.set('from', fromDate);
          if (toDate) p.set('to', toDate);
          const sortStr = buildSortParam();
        if (sortStr) p.set('sort', sortStr);
        p.set('limit', String(limit));
        p.set('offset', String(offset));
        return p.toString();
      };

      const PAGE = 1000;
      let offset = 0;
      let total = 0;
      const all = [];

      // Prime request to get total
      {
        const res = await fetch(`${API}/api/inventory?${buildParams(PAGE, 0)}`);
        if (!res.ok) throw new Error('Failed fetching inventory');
        const json = await res.json();
        const data = Array.isArray(json?.data) ? json.data : [];
        total = Number(json?.meta?.total || data.length || 0);
        all.push(...data);
        offset = data.length;
      }

      while (offset < total) {
        const res = await fetch(`${API}/api/inventory?${buildParams(PAGE, offset)}`);
        if (!res.ok) break;
        const json = await res.json();
        const data = Array.isArray(json?.data) ? json.data : [];
        if (!data.length) break;
        all.push(...data);
        offset += data.length;
      }

      const csvRows = all.map(r => [
        r.inventoryid,
        r.clientname ?? r.clientid ?? '',
        r.sku ?? '',
        r.description ?? '',
        r.inv_description ?? '',
        r.partnumber ?? '',
        r.receiptid ?? '',
        r.palletnumber ?? r.palletid ?? '',
        r.location ?? '',
        r.upc ?? '',
        r.recvdate ? new Date(r.recvdate).toISOString().slice(0,10) : '',
        r.qtyunits ?? '',
        r.qtyperunit ?? '',
        r.qtyunitsonhand ?? '',
        r.piecesonhand ?? '',
        r.expiration ? new Date(r.expiration).toISOString().slice(0,10) : '',
        r.lotnum ?? '',
        r.areaname ?? '',
        r.warehousename ?? '',
        r.storageunitname ?? '',
        r.packaging ?? ''
      ]);

      const csv = [headers.map(esc).join(','), ...csvRows.map(r => r.map(esc).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory-export-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      alert('Export failed: ' + (e?.message || e));
    } finally {
      setExporting(false);
    }
  };

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Inventory ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);

  // Multi-sort: single click sets primary; Shift+Click adds/toggles secondary
  const handleSort = (field, e) => {
    if (resizedRecentlyRef.current) return;
    if (e?.target && typeof e.target.closest === 'function' && e.target.closest('.col-resizer')) return;
    setSorts(prev => {
      const idx = prev.findIndex(s => s.field === field);
      let next = [];
      if (e?.shiftKey) {
        if (idx === -1) next = [...prev, { field, order: 'ASC' }];
        else next = prev.map((s, i) => i === idx ? { ...s, order: s.order === 'ASC' ? 'DESC' : 'ASC' } : s);
      } else {
        if (idx === 0) next = [{ field, order: prev[0].order === 'ASC' ? 'DESC' : 'ASC' }];
        else next = [{ field, order: 'ASC' }];
      }
      return next;
    });
    // clear and fetch first page
    setRows([]); setMeta({ total: 0, limit, offset: 0 });
    fetchInventory(0, limit);
  };

  const getSortInfo = (field) => {
    const idx = sorts.findIndex(s => s.field === field);
    if (idx === -1) return null;
    return { order: sorts[idx].order, idx: idx + 1 };
  };

  const lockedClientName = useMemo(() => {
    if (!IS_CLIENT || !LOCKED_CLIENT_ID) return '';
    const hit = clientOptions.find(c => String(c.id) === String(LOCKED_CLIENT_ID));
    return hit?.name || LOCKED_CLIENT_NAME_FALLBACK;
  }, [IS_CLIENT, LOCKED_CLIENT_ID, clientOptions]);

  // Receipts-like header indicator: show only the primary sort arrow
  const getPrimarySortOrder = (field) => {
    if (!field || !Array.isArray(sorts) || !sorts.length) return null;
    const primary = sorts[0];
    if (primary?.field === field) return primary.order;
    return null;
  };
  const renderSortHeader = (label, field) => {
    const order = getPrimarySortOrder(field);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label} {order ? (order === 'ASC' ? '▲' : '▼') : ''}
      </span>
    );
  };

  // ====== Columns (reorderable + persisted)
  const baseColumns = useMemo(() => {
    const cols = [
      { key: 'actions' },
      { key: 'clientname', sort: 'clientname', label: 'Client' },
      { key: 'image', label: 'Image' },
      { key: 'sku', sort: 'sku', label: 'SKU' },
      { key: 'description', sort: 'description', label: 'Description' },
      { key: 'inv_description', sort: 'inv_description', label: 'Inventory Notes' },
      { key: 'partnumber', sort: 'partnumber', label: 'Part #' },
      { key: 'receiptid', sort: 'receiptid', label: 'WR #' },
      { key: 'palletnumber', sort: 'palletnumber', label: 'Storage Unit #' },
      { key: 'location', sort: 'location', label: 'Location' },
      { key: 'upc', sort: 'upc', label: 'UPC' },
      { key: 'recvdate', sort: 'recvdate', label: 'Recv Date' },
      { key: 'ponum', sort: 'ponum', label: 'PO' },
      { key: 'lotnum', sort: 'lotnum', label: 'Lot' },
      { key: 'warehousename', sort: 'warehousename', label: 'Warehouse' },
      { key: 'storageunitname', sort: 'storageunitname', label: 'Storage Unit Name' },
      // Packaging unit (same as Receive.jsx "Packaging Unit Type")
      { key: 'packaging', sort: 'packaging', label: 'Packaging Unit Type' },
      { key: 'areaname', sort: 'areaname', label: 'Area' },
      { key: 'qtyunitsonhand', sort: 'qtyunitsonhand', label: 'Units On Hand' },
      { key: 'piecesonhand', sort: 'piecesonhand', label: 'Pieces On Hand' },
      { key: 'expiration', sort: 'expiration', label: 'Exp' }
    ];
    // Hide Actions column for client users
    return IS_CLIENT ? cols.filter(c => c.key !== 'actions') : cols;
  }, [IS_CLIENT]);

  const getInitialColumns = () => {
    let order = [];
    try { order = JSON.parse(localStorage.getItem(LS_COLORDER_KEY) || '[]'); } catch {}
    const byKey = Object.fromEntries(baseColumns.map(c => [c.key, c]));
    const picked = order.map(k => byKey[k]).filter(Boolean);
    const remaining = baseColumns.filter(c => !picked.find(p => p.key === c.key));
    let arr = [...picked, ...remaining];
    // Ensure Actions is always the first column for non-clients
    if (!IS_CLIENT) {
      const idx = arr.findIndex(c => c.key === 'actions');
      if (idx > 0) {
        const [act] = arr.splice(idx, 1);
        arr = [act, ...arr];
      }
    }
    return arr;
  };
  const [columns, setColumns] = useState(getInitialColumns());
  useEffect(() => {
    try { localStorage.setItem(LS_COLORDER_KEY, JSON.stringify(columns.map(c => c.key))); } catch {}
  }, [columns]);
  const [hiddenCols, setHiddenCols] = useState(() => new Set(getInitialHiddenCols()));
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsBtnRef = useRef(null);
  const columnsPopoverRef = useRef(null);
  useEffect(() => {
    setHiddenCols(prev => {
      const columnKeys = new Set(columns.map(c => c.key));
      const filtered = Array.from(prev).filter(k => columnKeys.has(k));
      let changed = filtered.length !== prev.size;
      if (columns.length > 0 && columns.length - filtered.length <= 0) {
        filtered.pop();
        changed = true;
      }
      return changed ? new Set(filtered) : prev;
    });
  }, [columns]);
  useEffect(() => {
    try { localStorage.setItem(LS_HIDDEN_COLS_KEY, JSON.stringify(Array.from(hiddenCols))); } catch {}
  }, [hiddenCols]);
  useEffect(() => {
    if (!columnsOpen) return;
    const onDoc = (e) => {
      const target = e.target;
      if (columnsPopoverRef.current && columnsPopoverRef.current.contains(target)) return;
      if (columnsBtnRef.current && columnsBtnRef.current.contains(target)) return;
      setColumnsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [columnsOpen]);
  const [colWidths, setColWidths] = useState(getInitialColWidths()); // key -> px
  const resizeStateRef = useRef({ key: null, startX: 0, startW: 0, neighborKey: null, neighborStartW: 0 });
  const resizedRecentlyRef = useRef(false);
  const headerRowRef = useRef(null);
  const tableRef = useRef(null);
  const visibleColumns = useMemo(() => columns.filter(c => !hiddenCols.has(c.key)), [columns, hiddenCols]);
  const toggleColumnVisibility = (key) => {
    setHiddenCols(prev => {
      if (prev.has(key)) {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }
      if (columns.length - prev.size <= 1) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };
  const showAllColumns = () => setHiddenCols(new Set());

  // Drag state for column reordering
  const dragColKeyRef = useRef(null);

  // Reset columns: order + widths
  const resetColumns = () => {
    try {
      localStorage.removeItem(LS_COLORDER_KEY);
      localStorage.removeItem(LS_COLWIDTHS_KEY);
    } catch {}
    setColumns([...baseColumns]);
    setColWidths({});
    setHiddenCols(new Set());
  };

  // Persist column widths whenever they change to avoid stale closures on mouseup
  useEffect(() => {
    try {
      window.localStorage.setItem(LS_COLWIDTHS_KEY, JSON.stringify(colWidths));
    } catch {}
  }, [colWidths]);

  const ensureColWidthsLocked = () => {
    const row = headerRowRef.current;
    if (!row) return;
    const ths = Array.from(row.children || []);
    if (!ths.length) return;
    // If already sized for all headers, skip
    if (Object.keys(colWidths || {}).length === ths.length) return;
    const next = {};
    ths.forEach((th) => {
      const key = th.getAttribute('data-colkey');
      if (key) next[key] = th.offsetWidth;
    });
    setColWidths(next);
  };

  const onResizeMouseMove = (e) => {
    const { key, startX, startW } = resizeStateRef.current;
    if (!key) return;
    const delta = e.clientX - startX;
    const nextW = Math.max(MIN_COL_W, startW + delta);
    setColWidths((prev) => ({ ...prev, [key]: nextW }));
    e.preventDefault();
  };
  const onResizeMouseUp = () => {
    resizeStateRef.current = { key: null, startX: 0, startW: 0, neighborKey: null, neighborStartW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onResizeMouseMove);
    window.removeEventListener('mouseup', onResizeMouseUp);
    // briefly suppress header clicks that might fire after resizing
    resizedRecentlyRef.current = true;
    setTimeout(() => { resizedRecentlyRef.current = false; }, 150);
    // persist latest widths
    try {
      window.localStorage.setItem(LS_COLWIDTHS_KEY, JSON.stringify(colWidths));
    } catch {}
  };
  const startResize = (key, e) => {
    ensureColWidthsLocked();
    const th = e.currentTarget.parentElement;
    resizeStateRef.current = {
      key,
      startX: e.clientX,
      startW: th.offsetWidth,
      neighborKey: null,
      neighborStartW: 0
    };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onResizeMouseMove);
    window.addEventListener('mouseup', onResizeMouseUp);
    e.preventDefault();
    e.stopPropagation();
  };

  const autoFitColumn = (key) => {
    try {
      const ths = Array.from(headerRowRef.current?.children || []);
      const idx = ths.findIndex(th => th.getAttribute('data-colkey') === key);
      if (idx === -1) return;
      let maxW = 0;
      // Measure header cell
      if (headerRowRef.current && ths[idx]) {
        const th = ths[idx];
        maxW = Math.max(maxW, Math.ceil(th.scrollWidth));
      }
      // Measure visible body cells
      const tableEl = tableRef.current;
      if (tableEl && tableEl.tBodies && tableEl.tBodies[0]) {
        const rows = tableEl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx];
          if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      const target = Math.max(MIN_COL_W, Math.min(800, maxW));
      setColWidths(prev => {
        const next = { ...prev, [key]: target };
        try { window.localStorage.setItem(LS_COLWIDTHS_KEY, JSON.stringify(next)); } catch {}
        return next;
      });
    } catch {}
  };

  const handleLimitUpdate = (raw) => {
    let v = parseInt(raw ?? limitInput, 10);
    if (isNaN(v)) v = limit;
    if (v < 1) v = 1;
    if (v > 10000) v = 10000;
    setLimit(v);
    setLimitInput(String(v));
    window.localStorage.setItem(LS_LIMIT_KEY, v);
    setRows([]); setMeta({ total: 0, limit: v, offset: 0 });
    fetchInventory(0, v);
  };

  // Totals for visible rows
  const totals = useMemo(() => {
    try {
      const units = (rows || []).reduce((acc, r) => acc + (Number(r?.qtyunitsonhand) || 0), 0);
      const pieces = (rows || []).reduce((acc, r) => acc + (Number(r?.piecesonhand) || 0), 0);
      return { units, pieces };
    } catch {
      return { units: 0, pieces: 0 };
    }
  }, [rows]);
  const totalsLabelKey = useMemo(() => {
    if (!visibleColumns.length) return null;
    return visibleColumns.some(c => c.key === 'sku') ? 'sku' : visibleColumns[0].key;
  }, [visibleColumns]);

  // Resolve a small thumbnail from common fields (URL / data URL / base64 / Buffer-like)
  const resolveItemThumb = (row) => {
    const isDataUrl = (s) => typeof s === 'string' && s.trim().startsWith('data:image/');
    const isHttpUrl = (s) => typeof s === 'string' && (/^https?:\/\//i.test(s) || s.startsWith('/'));
    const isB64 = (s) => typeof s === 'string' && s.replace(/\s+/g, '').length > 64 && /^[A-Za-z0-9+/=]+$/.test(s.replace(/\s+/g, ''));

    // 1) URL-ish fields first
    const urlFields = ['itemimageurl','item_image_url','imageurl','image_url','imageUrl','thumb','thumbnail'];
    for (const f of urlFields) {
      const v = row?.[f];
      if (v && (isHttpUrl(v) || isDataUrl(v))) return String(v).trim();
    }

    // 2) Common base64/buffer fields
    const baseFields = ['image1','itemimage1','image','img','image_base64','imageB64'];
    for (const f of baseFields) {
      const v = row?.[f]; if (!v) continue;
      if (typeof v === 'string') {
        const s = v.trim();
        if (isDataUrl(s)) return s;
        if (isB64(s)) return `data:image/jpeg;base64,${s.replace(/\s+/g, '')}`;
      }
      if (v && typeof v === 'object') {
        // Node Buffer-like
        const d = Array.isArray(v?.data) ? v.data : (Array.isArray(v) ? v : null);
        if (d) {
          try { const u8 = new Uint8Array(d); const blob = new Blob([u8], { type: 'image/jpeg' }); return URL.createObjectURL(blob); }
          catch { /* ignore */ }
        }
      }
    }
    return '';
  };

  // Apply date preset (sets from/to, persists preset, clears and fetches)
  const applyDatePreset = (preset) => {
    const today = new Date();
    let from = '', to = '';

    switch (preset) {
      case 'today': {
        const t = fmtYMD(today); from = t; to = t; break;
      }
      case 'yesterday': {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        const t = fmtYMD(y); from = t; to = t; break;
      }
      case 'this_week': {
        const s = startOfWeekMon(today); from = fmtYMD(s); to = fmtYMD(today); break;
      }
      case 'last_week': {
        const s0 = startOfWeekMon(today);
        const s = new Date(s0); s.setDate(s0.getDate() - 7);
        const e = new Date(s);  e.setDate(s.getDate() + 6);
        from = fmtYMD(s); to = fmtYMD(e); break;
      }
      case 'this_month': {
        const s = startOfMonth(today); from = fmtYMD(s); to = fmtYMD(today); break;
      }
      case 'last_month': {
        const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        const e = endOfMonth(s); from = fmtYMD(s); to = fmtYMD(e); break;
      }
      case 'this_year': {
        const s = startOfYear(today.getFullYear()); from = fmtYMD(s); to = fmtYMD(today); break;
      }
      case 'last_year': {
        const y = today.getFullYear() - 1;
        from = fmtYMD(startOfYear(y)); to = fmtYMD(endOfYear(y)); break;
      }
      default: {
        localStorage.setItem(LS_DATE_PRESET, '');
        setDatePreset('');
        return;
      }
    }

    setFromDate(from);
    setToDate(to);
    setDatePreset(preset);
    localStorage.setItem(LS_DATE_PRESET, preset);

    setRows([]); setMeta({ total: 0, limit, offset: 0 });
    fetchInventory(0, limit);
  };

  return (
    <div className="inventory-wrapper">
      <div className="inventory-toolbar">
        <div className="search">
          <FaSearch />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search SKU / Description / Lot / PO / WR#"
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); setRows([]); setMeta({ total: 0, limit, offset: 0 }); fetchInventory(0, limit); }
            }}
          />
          {q && (
            <button className="clear" onClick={() => { setQ(''); setRows([]); setMeta({ total: 0, limit, offset: 0 }); fetchInventory(0, limit); }}>
              <FaTimes />
            </button>
          )}
        </div>

        {!IS_CLIENT && (
          <label className="client-group">
            Client:
            <select value={client} onChange={e => { setClient(e.target.value); try { localStorage.setItem(LS_CLIENT_KEY, e.target.value || ''); } catch {}; setRows([]); setMeta({ total: 0, limit, offset: 0 }); }}>
              <option value="">All</option>
              {clientOptions.map(cl => (
                <option key={cl.id} value={cl.id}>{cl.name}</option>
              ))}
            </select>
          </label>
        )}

        {IS_CLIENT && clientOptions.length > 1 && (
          <label className="client-group">
            Client:
            <select
              value={client}
              onChange={e => {
                const val = e.target.value;
                setClient(val);
                try { localStorage.setItem(LS_CLIENT_KEY, val || ''); } catch {}
                setRows([]);
                setMeta({ total: 0, limit, offset: 0 });
                fetchInventory(0, limit);
              }}
            >
              <option value="">All</option>
              {clientOptions.map(cl => (
                <option key={cl.id} value={cl.id}>{cl.name}</option>
              ))}
            </select>
          </label>
        )}

        <label>
          Storage Unit:
          <select
            value={storageUnitId}
            onChange={e => {
              const val = e.target.value;
              setStorageUnitId(val);
              try { localStorage.setItem(LS_STORAGEUNIT_KEY, val || ''); } catch {}
              setRows([]); setMeta({ total: 0, limit, offset: 0 }); fetchInventory(0, limit);
            }}
          >
            <option value="">All</option>
            {storageUnitOptions.map(su => (
              <option key={su.id} value={su.id}>{su.name}</option>
            ))}
          </select>
        </label>

        {/* NEW: Date preset dropdown (before From/To) */}
        <label>
          Preset:
          <select
            value={datePreset}
            onChange={(e) => applyDatePreset(e.target.value)}
          >
            <option value="">Custom…</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
          </select>
        </label>

        <label>
          From:
          <input
            type="date"
            value={fromDate}
            onChange={e => {
              setFromDate(e.target.value);
              setDatePreset(''); localStorage.setItem(LS_DATE_PRESET, '');
              try { localStorage.setItem(LS_FROM_KEY, e.target.value || ''); } catch {}
              setRows([]); setMeta({ total: 0, limit, offset: 0 }); fetchInventory(0, limit);
            }}
          />
        </label>
        <label>
          To:
          <input
            type="date"
            value={toDate}
            onChange={e => {
              setToDate(e.target.value);
              setDatePreset(''); localStorage.setItem(LS_DATE_PRESET, '');
              try { localStorage.setItem(LS_TO_KEY, e.target.value || ''); } catch {}
              setRows([]); setMeta({ total: 0, limit, offset: 0 }); fetchInventory(0, limit);
            }}
          />
        </label>

        <button className="btn" onClick={() => { setRows([]); setMeta({ total: 0, limit, offset: 0 }); fetchInventory(0, limit); }}>
          <FaSearch style={{ marginRight: 6 }} /> Search
        </button>
        <button className="btn" onClick={exportInventoryCsv} disabled={exporting} title="Export current inventory to CSV" style={{ marginLeft: 8 }}>
          <FaFileCsv style={{ marginRight: 6 }} /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
        <button className="btn" onClick={resetColumns} title="Reset column order and widths" style={{ marginLeft: 8 }}>
          <FaColumns style={{ marginRight: 6 }} /> Reset Columns
        </button>
        <div className="inventory-cols-controls">
          <button
            className="btn"
            ref={columnsBtnRef}
            onClick={() => setColumnsOpen(v => !v)}
            title="Show or hide columns"
            type="button"
          >
            <FaEyeSlash style={{ marginRight: 6 }} /> Columns
          </button>
          {columnsOpen && (
            <div className="inventory-cols-popover" ref={columnsPopoverRef}>
              <div className="inventory-cols-head">Visible columns</div>
              <ul>
                {columns.map(col => {
                  const disabled = !hiddenCols.has(col.key) && visibleColumns.length <= 1;
                  return (
                    <li key={col.key}>
                      <label>
                        <input
                          type="checkbox"
                          checked={!hiddenCols.has(col.key)}
                          disabled={disabled}
                          title={disabled ? 'At least one column must remain visible' : undefined}
                          onChange={() => toggleColumnVisibility(col.key)}
                        />
                        <span>{col.label ? col.label : (col.key === 'actions' ? 'Actions' : col.key.replace(/_/g, ' ').replace(/\b\w/g, (txt) => txt.toUpperCase()))}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              <div className="inventory-cols-actions">
                <button type="button" className="mini-btn" onClick={showAllColumns}>Show All</button>
                <button type="button" className="mini-btn" onClick={() => setColumnsOpen(false)}>Close</button>
              </div>
            </div>
          )}
        </div>
        {/* Picking Replenish button removed per request */}
      </div>

      <div className="inventory-content">
        <Split
          className="inventory-vertical-split"
          direction="vertical"
          sizes={bottomOpen ? splitSizes : [100, 0]}
          minSize={bottomOpen ? [200, 200] : [200, 0]}
          gutterSize={6}
          onDragEnd={(sizes) => { setSplitSizes(sizes); try { localStorage.setItem(INV_SPLIT_KEY, JSON.stringify(sizes)); } catch {} }}
          style={{ height: '100%' }}
        >
        <div className="inv-top-pane">
          <div className="inventory-table-wrapper">
          <table className="inventory-table" ref={tableRef}>
            <colgroup>
              {visibleColumns.map(c => (
                <col key={c.key} style={{ width: colWidths[c.key] ? `${colWidths[c.key]}px` : undefined }} />
              ))}
            </colgroup>
            <thead>
              <tr ref={headerRowRef}>
                {visibleColumns.map(col => {
                  if (col.key === 'actions') {
                    return (
                      <th
                        key={col.key}
                        data-colkey={col.key}
                        style={{ position: 'relative' }}
                        draggable={false}
                      >
                        Actions
                        <span
                          className="col-resizer"
                          onMouseDown={(e)=>startResize(col.key, e)}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitColumn(col.key); }}
                        />
                      </th>
                    );
                  }
                  const order = col.sort ? getPrimarySortOrder(col.sort) : null;
                  return (
                    <th
                      key={col.key}
                      data-colkey={col.key}
                      onClick={col.sort ? (e)=>handleSort(col.sort, e) : undefined}
                      className={order ? 'active-sort' : ''}
                      style={{
                        cursor: col.sort ? 'pointer' : 'default',
                        userSelect: 'none',
                        position: 'relative',
                        color: order ? 'var(--primary-color)' : undefined,
                        textDecoration: order ? 'underline' : undefined,
                      }}
                      title={order ? `Sorted ${order}` : (col.sort ? 'Click to sort. Shift+Click to add secondary sort' : '')}
                      draggable
                      onDragStart={(e) => { dragColKeyRef.current = col.key; e.dataTransfer.effectAllowed = 'move'; }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const fromKey = dragColKeyRef.current;
                        const toKey = col.key;
                        dragColKeyRef.current = null;
                        if (!fromKey || fromKey === toKey) return;
                        setColumns(prev => {
                          const fromIdx = prev.findIndex(c => c.key === fromKey);
                          const toIdx = prev.findIndex(c => c.key === toKey);
                          if (fromIdx === -1 || toIdx === -1) return prev;
                          // Prevent moving 'actions'
                          if (prev[toIdx].key === 'actions' || prev[fromIdx].key === 'actions') return prev;
                          const next = [...prev];
                          const [moved] = next.splice(fromIdx, 1);
                          // insert before target index (after removal, adjust index if needed)
                          const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;
                          next.splice(insertAt, 0, moved);
                          return next;
                        });
                      }}
                    >
                      {renderSortHeader(col.label || '', col.sort)}
                      <span
                        className="col-resizer"
                        onMouseDown={(e)=>startResize(col.key, e)}
                        onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                        onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitColumn(col.key); }}
                      />
                    </th>
                  );
                })}
              </tr>
          </thead>
          <tbody>
              {loading ? (
                <tr><td colSpan={Math.max(visibleColumns.length, 1)}>Loading…</td></tr>
              ) : rows.length ? (
                rows.map(row => (
                  <tr
                    key={row.inventoryid}
                    className={selectedRow?.inventoryid === row.inventoryid ? 'active' : ''}
                    onClick={() => setSelectedRow(row)}
                    style={{ cursor: 'pointer' }}
                  >
                    {visibleColumns.map(col => (
                      <td key={col.key}>
                        {(() => {
                          switch (col.key) {
                            case 'actions':
                              return (
                                <div className="actions">
                                  <button
                                    className="mini-btn"
                                    title="View History"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      try { setSelectedRow(row); } catch {}
                                      try { setBottomOpen(true); } catch {}
                                    }}
                                  >
                                    <FaHistory size={14} />
                                  </button>
                                  <button className="mini-btn" title="Print Label" onClick={(e) => { e.stopPropagation(); printPalletLabel(row.inventoryid); }}>
                                    <FaTag size={14} />
                                  </button>
                                  <button
                                    className="mini-btn"
                                    title="Open Inventory PDF"
                                    onClick={(e) => { e.stopPropagation(); window.open(`${API}/api/inventory/${row.inventoryid}/pdf`, '_blank'); }}
                                  >
                                    <FaFilePdf size={14} />
                                  </button>
                                </div>
                              );
                            case 'clientname': return (row.clientname || row.clientid);
                            case 'image': {
                              const src = resolveItemThumb(row);
                              return src ? (
                                <img
                                  src={src}
                                  alt={row.sku || 'thumb'}
                                  className="inv-thumb"
                                  loading="lazy"
                                  onMouseEnter={(e) => showPreview(row.image1 || row.itemimageurl || src, e)}
                                  onMouseMove={movePreview}
                                  onMouseLeave={hidePreview}
                                />
                              ) : (<span style={{ opacity: 0.6 }}>—</span>);
                            }
                            case 'sku': return row.sku;
                            case 'description': return row.description;
                            case 'inv_description': return (row.inv_description || '');
                            case 'partnumber': return (row.partnumber || '');
                            case 'receiptid':
                              if (!row.receiptid) return '';
                              if (IS_CLIENT) {
                                return `WR${row.receiptid}`;
                              }
                              return (
                                <a
                                  href={`/receive/${row.receiptid}?inv=${row.inventoryid}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  WR{row.receiptid}
                                </a>
                              );
                          case 'palletnumber': return (row.palletnumber || row.palletid);
                          case 'location': return (row.location || '');
                          case 'upc': return (row.upc || '');
                          case 'recvdate': return (row.recvdate ? new Date(row.recvdate).toLocaleDateString() : '');
                            case 'ponum': return row.ponum;
                            case 'lotnum': return row.lotnum;
                            case 'warehousename': return (row.warehousename || '');
                            case 'storageunitname': return (row.storageunitname || row.storageunitid);
                            case 'areaname': return (row.areaname || '');
                            case 'qtyunitsonhand': return row.qtyunitsonhand;
                            case 'piecesonhand': return row.piecesonhand;
                            case 'expiration': return (row.expiration ? new Date(row.expiration).toLocaleDateString() : '');
                            default:
                              return row[col.key] ?? '';
                          }
                        })()}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={Math.max(visibleColumns.length, 1)}>No inventory found.</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                {visibleColumns.map(col => {
                  if (col.key === 'qtyunitsonhand') {
                    return <td key={col.key} style={{ fontWeight: 700 }}>{Number(totals.units || 0).toLocaleString()}</td>;
                  }
                  if (col.key === 'piecesonhand') {
                    return <td key={col.key} style={{ fontWeight: 700 }}>{Number(totals.pieces || 0).toLocaleString()}</td>;
                  }
                  if (totalsLabelKey && col.key === totalsLabelKey) {
                    const foundText = meta?.total ? `(${Number(meta.total).toLocaleString()} found)` : '';
                    return (
                      <td key={col.key} style={{ fontWeight: 700, textAlign: 'right' }}>
                        Totals {foundText ? ` ${foundText}` : ''}:
                      </td>
                    );
                  }
                  return <td key={col.key} />;
                })}
              </tr>
            </tfoot>
          </table>
          {preview.visible && preview.src && (
            <div className="inv-preview" ref={previewRef} style={{ left: preview.x, top: preview.y }}>
              <img src={preview.src} alt="" onLoad={onPreviewImgLoad} />
            </div>
          )}
          {loading && (
            <div className="inv-loading-backdrop" role="dialog" aria-modal="true" aria-label="Loading inventory">
              <div className="inv-loading-modal">
                <div className="inv-spinner" />
                <div className="inv-loading-text">Loading inventory…</div>
              </div>
            </div>
          )}
          </div>

          <div className="pagination">
          <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
          <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
          <span>Page {page} of {lastPage}</span>
          <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
          <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>
          <label style={{ marginLeft: 12, fontSize: 13 }}>
            Rows per page:&nbsp;
            <input
              type="number"
              min={1}
              max={10000}
              value={limitInput}
              style={{ width: 60, fontSize: 13, padding: "2px 4px" }}
              onKeyDown={e => { if (e.key === 'Enter') handleLimitUpdate(e.currentTarget.value); }}
              onChange={e => setLimitInput(e.target.value)}
              onBlur={e => handleLimitUpdate(e.target.value)}
            />
          </label>
          </div>
        </div>
        {/* Bottom pane: histories */}
        <div className="inventory-bottom-pane" style={{ display: bottomOpen ? 'flex' : 'none', flexDirection: 'column', minHeight: 0 }}>
          <div className="bottom-pane-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
            <strong>
              Details for SKU {selectedRow?.sku ?? ''} - {
                selectedRow?.receiptid ? (
                  <a
                    href={`/receive/${selectedRow.receiptid}?inv=${selectedRow.inventoryid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    WR{selectedRow.receiptid}
                  </a>
                ) : (
                  'WR'
                )
              } - Pallet #{selectedRow?.palletnumber ?? selectedRow?.palletid ?? ''}
            </strong>
            <div style={{ flex: 1 }} />
            <button className="btn" onClick={() => setBottomOpen(false)}>
              <FaTimes style={{ marginRight: 6, verticalAlign: '-2px' }} />
              Close
            </button>
          </div>
          <div className="split-history">
            <div className="history-panel">
              <h3>Inventory History</h3>
              {invHistoryLoading ? (
                <div className="loading">Loading…</div>
              ) : invHistory.length ? (
                <table className="history-table">
                  <thead>
                    <tr><th>Date</th><th>User</th><th>Log</th></tr>
                  </thead>
                  <tbody>
                    {invHistory.map(h => (
                      <tr
                        key={h.id}
                        className={selectedInvHistId === h.id ? 'active' : ''}
                        onClick={() => setSelectedInvHistId(h.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{h.date ? new Date(h.date).toLocaleString() : ''}</td>
                        <td>{h.username || ''}</td>
                        <td>{h.log}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div>No history.</div>
              )}
            </div>
            <div className="history-panel">
              <h3>Storage Location History</h3>
              {palletHistoryLoading ? (
                <div className="loading">Loading…</div>
              ) : palletHistory.length ? (
                <table className="history-table">
                  <thead>
                    <tr><th>Date</th><th>User</th><th>Storage Location</th><th>Log</th></tr>
                  </thead>
                  <tbody>
                    {palletHistory.map(h => (
                      <tr
                        key={h.id}
                        className={selectedPalletHistId === h.id ? 'active' : ''}
                        onClick={() => setSelectedPalletHistId(h.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{h.date ? new Date(h.date).toLocaleString() : ''}</td>
                        <td>{h.username || h.userid || ''}</td>
                        <td>{h.locationlabel || (h.locationid ? String(h.locationid) : 'Floor')}</td>
                        <td>{h.log}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div>No pallet location history.</div>
              )}
            </div>
          </div>
        </div>
        </Split>
      </div>
    </div>
  );
}
